import { ImpactIndicator, RegionSetStatusType } from "@app/projects/enumerators";
import { AggregateDisplayType, SpecialtyGroupEntityType } from "@app/projects/literals";
import {
  AccessSummary,
  AdequacySummarySpecialtyGroup,
  MarketSummary,
  ServicingSummary,
  SpecialtyDetailFootnote,
} from "@app/projects/results/adequacy";
import {
  AnalysisSpecialtyGroup,
  ResultRegion,
  SpecialtyGroupException,
} from "@app/projects/results/common";
import { ZipSummariesAPIResponseModel } from "@app/projects/zip-summaries/interfaces";
import { RegionGranularity } from "@app/region-sets";
import { GapFillStatusEnum } from "./";

interface AdequacyDetail {
  key: string;
  defaultDisplayOrder: number | string;
  displayText: string;
  linkSuffix: string;

  regionCode: string;
  regionSetId: number;
  regionSetDisplayName: string;
  regionGranularity: RegionGranularity;
  subRegionCode: string;
  regionSetStatus: RegionSetStatusType;

  specialtyGroupCode: string;
  specialtyGroupDisplayName: string;
  specialtyGroupDisplayOrder: number;
  specialtyGroupEntityType?: SpecialtyGroupEntityType;
  specialtyGroupIsInformational: boolean;
  specialtyGroupName?: string;
  canExpandSpecialties?: boolean;
  suppressSpecialtyAccessResults?: boolean;

  hideSpecialtyMarketAnalysis?: boolean;
  specialtyCode?: string;

  dateGapStarted?: Date;
  gapAgeDays?: number;
  impactIndicator: ImpactIndicator;
  metCriteria: boolean;

  //TODO: Flatten these out...yeah there will be a lot of properties in this interface, but it's worth it
  accessSummary: AccessSummary;
  marketSummary: MarketSummary;
  servicingSummary: ServicingSummary;

  recentlyAttestedUniqueNPICount?: number;
  recentlyAttestedUniqueNPIPercent?: number;

  exception?: SpecialtyGroupException | null;

  footnote: SpecialtyDetailFootnote;

  parentAbbreviation?: string;
  parentDisplayName?: string;
  isExpandedSpecialty?: boolean;
  accessTelehealthCredit?: number;
  conAccessCreditPercentage?: number;
  applicationCreditMetCriteria?: boolean;

  zipSummaries?: ZipSummariesAPIResponseModel[];
  fillableGapIndicator?: string;

  gapFillStatus?: GapFillStatusEnum;
  gapFillStatusUpdatedByName?: string;
  gapFillStatusUpdatedDate?: Date;
  gapFillStatusComment?: string;

  targetProvidersCount?: number;
}

class AdequacyDetailImpl implements AdequacyDetail {
  public constructor(
    public key: string,
    private aggregateDisplayType: AggregateDisplayType,

    public regionCode: string,
    public regionGranularity: RegionGranularity,
    public regionSetDisplayName: string,
    public regionSetId: number,
    public subRegionCode: string,
    public regionSetStatus: RegionSetStatusType,

    public specialtyGroupCode: string,
    public specialtyGroupDisplayName: string,
    public specialtyGroupDisplayOrder: number,
    public specialtyGroupIsInformational: boolean,
    public canExpandSpecialties: boolean,
    public suppressSpecialtyAccessResults: boolean,

    public impactIndicator: ImpactIndicator,
    public metCriteria: boolean,

    public accessSummary: AccessSummary,
    public marketSummary: MarketSummary,
    public servicingSummary: ServicingSummary,

    public footnote: SpecialtyDetailFootnote,

    public hideSpecialtyMarketAnalysis?: boolean,
    public specialtyCode?: string,

    public recentlyAttestedUniqueNPICount?: number,
    public recentlyAttestedUniqueNPIPercent?: number,

    public dateGapStarted?: Date,
    public exception?: SpecialtyGroupException | null,
    public gapAgeDays?: number,
    public specialtyGroupEntityType?: SpecialtyGroupEntityType,
    public specialtyGroupName?: string,

    public parentAbbreviation?: string,
    public parentDisplayName?: string,
    public isExpandedSpecialty?: boolean,
    public accessTelehealthCredit?: number,
    public conAccessCreditPercentage?: number,
    public applicationCreditMetCriteria?: boolean,

    public zipSummaries?: ZipSummariesAPIResponseModel[],
    public fillableGapIndicator?: string,

    public gapFillStatus?: GapFillStatusEnum,
    public gapFillStatusUpdatedByName?: string,
    public gapFillStatusUpdatedDate?: Date,
    public gapFillStatusComment?: string,

    public targetProvidersCount?: number
  ) { }

  public get defaultDisplayOrder(): number | string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayOrder;
      case "unknown":
        return null;
    }
  }

  public get displayText(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayName;
      case "unknown":
        return null;
    }
  }

  public get linkSuffix(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return `/${this.regionGranularity}/${this.regionCode}`;
      case "specialty":
        return `/specialty/${encodeURIComponent(this.specialtyGroupCode)}`;
      case "unknown":
        return null;
    }
  }
}

function buildAdequacyDetail(
  key: string,
  resultRegion: ResultRegion,
  adequacySummarySpecialtyGroup: AdequacySummarySpecialtyGroup,
  specialtyGroup: AnalysisSpecialtyGroup,
  exception: SpecialtyGroupException,
  aggregateDisplayType: AggregateDisplayType,
): AdequacyDetail {
  if (!resultRegion || !adequacySummarySpecialtyGroup || !specialtyGroup) {
    return {} as AdequacyDetail;
  }

  return new AdequacyDetailImpl(
    key,
    aggregateDisplayType,

    resultRegion.regionCode,
    resultRegion.regionGranularity,
    resultRegion.displayName,
    resultRegion.regionSetId,
    resultRegion.subRegionCode,
    adequacySummarySpecialtyGroup.regionSetStatus || RegionSetStatusType.Unknown,

    specialtyGroup.code,
    `${specialtyGroup.code} - ${specialtyGroup.name}`,
    specialtyGroup.displayOrder,
    specialtyGroup.isInformational,
    adequacySummarySpecialtyGroup.canExpandSpecialties,
    specialtyGroup.suppressSpecialtyAccessResults,

    adequacySummarySpecialtyGroup.impactIndicator,
    adequacySummarySpecialtyGroup.metCriteria,

    adequacySummarySpecialtyGroup.accessSummary,
    adequacySummarySpecialtyGroup.marketSummary,
    adequacySummarySpecialtyGroup.servicingSummary,

    adequacySummarySpecialtyGroup.footnote,

    undefined, //specialty-level only
    undefined, //specialty-level only

    undefined,
    undefined,

    adequacySummarySpecialtyGroup.dateGapStarted,
    exception,
    adequacySummarySpecialtyGroup.gapAgeDays,

    specialtyGroup.entityType,
    specialtyGroup.name,

    undefined,
    undefined,
    undefined,
    adequacySummarySpecialtyGroup.accessTelehealthCredit,
    adequacySummarySpecialtyGroup.conAccessCreditPercentage,
    adequacySummarySpecialtyGroup.applicationCreditMetCriteria,

    adequacySummarySpecialtyGroup.zipSummaries,
    adequacySummarySpecialtyGroup.fillableGapIndicator,

    adequacySummarySpecialtyGroup.gapFillStatus,
    adequacySummarySpecialtyGroup.gapFillStatusUpdatedByName,
    adequacySummarySpecialtyGroup.gapFillStatusUpdatedDate,
    adequacySummarySpecialtyGroup.gapFillStatusComment,

  );
}

/**
 * Clones an `AdequacyDetail` while setting a new displayText property based on the `AdequacyAggregateDisplayType`
 * It DOES actually create a new object
 * @param adequacyDetail
 * @param aggregateDisplayType
 */
function psuedoClone(
  adequacyDetail: AdequacyDetail,
  aggregateDisplayType: AggregateDisplayType,
): AdequacyDetail {

  return new AdequacyDetailImpl(
    adequacyDetail.key,
    aggregateDisplayType,

    adequacyDetail.regionCode,
    adequacyDetail.regionGranularity,
    adequacyDetail.regionSetDisplayName,
    adequacyDetail.regionSetId,
    adequacyDetail.subRegionCode,
    adequacyDetail.regionSetStatus || RegionSetStatusType.Unknown,

    adequacyDetail.specialtyGroupCode,
    adequacyDetail.specialtyGroupDisplayName,
    adequacyDetail.specialtyGroupDisplayOrder,
    adequacyDetail.specialtyGroupIsInformational,
    adequacyDetail.canExpandSpecialties,
    adequacyDetail.suppressSpecialtyAccessResults,

    adequacyDetail.impactIndicator,
    adequacyDetail.metCriteria,

    adequacyDetail.accessSummary,
    adequacyDetail.marketSummary,
    adequacyDetail.servicingSummary,

    adequacyDetail.footnote,

    adequacyDetail.hideSpecialtyMarketAnalysis,
    adequacyDetail.specialtyCode,

    adequacyDetail.recentlyAttestedUniqueNPICount,
    adequacyDetail.recentlyAttestedUniqueNPIPercent,

    adequacyDetail.dateGapStarted,
    adequacyDetail.exception,
    adequacyDetail.gapAgeDays,

    adequacyDetail.specialtyGroupEntityType,
    adequacyDetail.specialtyGroupName,

    adequacyDetail.parentAbbreviation,
    adequacyDetail.parentDisplayName,
    adequacyDetail.isExpandedSpecialty,
    adequacyDetail.accessTelehealthCredit,
    adequacyDetail.conAccessCreditPercentage,
    adequacyDetail.applicationCreditMetCriteria,

    adequacyDetail.zipSummaries,
    adequacyDetail.fillableGapIndicator,

    adequacyDetail.gapFillStatus,
    adequacyDetail.gapFillStatusUpdatedByName,
    adequacyDetail.gapFillStatusUpdatedDate,
    adequacyDetail.gapFillStatusComment,
  );

}

export { AdequacyDetail, buildAdequacyDetail, psuedoClone };
